import { SxProps, Theme } from '@mui/material';

/**
 * The shared frame for the patient/visit tab tables.
 *
 * `height: 100%` matters: these tables fill their card and let the body scroll,
 * so every ancestor has to carry a real height too.
 *
 * The column-header row is separated by a rule rather than a fill: it takes the
 * same `background.default` the rows sit on, and a bottom border. The card's
 * own header strip above it is `background.paper`, so a filled column row read
 * as one thick band with it.
 *
 * The colour is set rather than left transparent because these tables are
 * `stickyHeader` — the rows scroll underneath this row and would show through
 * it.
 *
 * `backgroundImage: none` is the part that actually does it. The theme paints
 * the `stickyHeader` variant with a flat `background.neutral` GRADIENT on top
 * of its background colour, so overriding the colour alone changes nothing
 * visible; the gradient simply covers it.
 */
export const TAB_TABLE_CONTAINER_SX: SxProps<Theme> = {
  height: 1,
  display: 'flex',
  flexDirection: 'column',
  '& .MuiTableCell-root.MuiTableCell-head': {
    backgroundColor: 'background.default',
    backgroundImage: 'none',
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    // Half the rows' vertical padding. A column name is one short line where a
    // row holds two, so the same padding gave the header more air than the rows
    // it labels — and the rule beneath it already separates the two.
    paddingTop: 1,
    paddingBottom: 1,
  },
};

/**
 * The scrolling body inside {@link TAB_TABLE_CONTAINER_SX}.
 *
 * Absolute rather than in flow: the rows have to scroll INSIDE the card, so the
 * body takes the frame's size instead of setting it. A table left in flow grows
 * the card to fit its rows, which pushes the footer off the bottom and hands the
 * scrolling to the page.
 */
export const TAB_TABLE_SCROLL_BODY_SX: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

/** Offered by every tab table's footer, so the choice does not change per tab. */
export const TAB_TABLE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/** What every tab table opens on. */
export const TAB_TABLE_DEFAULT_ROWS_PER_PAGE = 25;

/**
 * The footer under a tab table. `flexShrink: 0` keeps it from being squeezed by
 * the body above it, and the toolbar drops to the same 16px gutter the cells and
 * the card header stand on — MUI's own default is 16px on the left of a
 * `TablePagination` toolbar but 24px once it has a rows-per-page label.
 */
export const TAB_TABLE_PAGINATION_SX: SxProps<Theme> = {
  flexShrink: 0,
  '& .MuiToolbar-root': {
    paddingLeft: 2,
  },
};

export default TAB_TABLE_CONTAINER_SX;
